import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { IPageData, IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { TLoadDependeciesParams } from '@df/catalog/model';
import { NgInject } from '@df/ng/ng-inject';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { NBlogCategoryPageType } from '../../../cms/blog/page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from '../../../cms/blog/page/post-view/cms-blog-page-post-view.component';
import { NBlogRootPageType } from '../../../cms/blog/page/root-view/cms-blog-page-root-view.component';
import { NMaterialsCalculatorCms } from '../../../materials-calculator/materials-calculator-cms.config';
import { NMaterialCalculatorPageType } from '../../../materials-calculator/page/material-calculator-cms-page';
import { NPageStoreFinderPageType } from '../store-finder/page-store-finder.component';
import { LocalPageResource } from './local-page.resource';

export class LocalPage<T extends IPageData = IPageData> extends ICmsPage<T> {
    @NgInject(DfApiConfigService)
    private dfApiConfigService!: DfApiConfigService;

    @NgInject(ICmsBlockResource)
    private iCmsBlockResource!: ICmsBlockResource;

    /**
     * Linked posts by tag
     * blog-category type only
     */
    private tagLinkedPosts?: LocalPage<IPageDataV2>[] | undefined;

    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     */
    getBlogPostCategories(): LocalPage<IPageDataV2>[] | undefined {
        const ids = this.getBlogPostCategoryIds();
        return ids && (this.resources.page.getAllExisting(ids) as LocalPage<IPageDataV2>[]);
    }
    /**
     * Retrieves categories (tags) ids of a blog post
     * blog-post type only
     */
    getBlogPostCategoryIds(): number[] {
        if (this.data?.pageType === NBlogPostPageType.typeId && this.verbosity > 1) {
            return uniq(flatten([this.data?.parent, (this.data as IPageDataV2).typeData?.tags]).filter(i => !!i));
        }
        return [];
    }
    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     * @returns
     */
    getBlogCategoryPosts(): LocalPage<IPageDataV2>[] {
        if (this.verbosity < 2) {
            return [];
        }
        switch (this.data?.pageType) {
            case NBlogCategoryPageType.typeId:
                return (uniq([...(this.children || []), ...(this.tagLinkedPosts || [])]) as LocalPage<IPageDataV2>[]).filter(
                    i => i.data?.pageType === NBlogPostPageType.typeId
                );
            case NBlogRootPageType.typeId:
                return this.resources.page
                    .getAllExisting()
                    .filter(i => i.data?.pageType === NBlogPostPageType.typeId) as LocalPage<IPageDataV2>[];
            default:
                return [];
        }
    }

    getCalculatorPages(): LocalPage<IPageDataV2>[] {
        if (this.verbosity < 2) {
            return [];
        }
        return this.resources.page
            .getAllExisting()
            .filter(i => i.data?.pageType === NMaterialCalculatorPageType.typeId) as LocalPage<IPageDataV2>[];
    }

    /**
     * Extra dependencies
     */
    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const safeParams = { ...params, safe: true };
        if (this.verbosity > 1) {
            if (this.rawData?.pageType === NBlogPostPageType.typeId) {
                // for blog post load all categories
                params.loader.add(this.loadBlogPostCategories(safeParams));
            }
            if (this.rawData?.pageType === NBlogCategoryPageType.typeId) {
                // for blog category load root page too
                params.loader.add((this.resources.page as unknown as LocalPageResource).loadBlogRoot(2, safeParams));
            }
        }
        if (this.verbosity > 2) {
            const pageResuorce = this.resources.page as any as LocalPageResource;
            switch (this.rawData?.pageType) {
                case NPageStoreFinderPageType.typeId:
                    params.loader.add(this.app.resources.store.loadAll());
                    params.loader.add(this.dfApiConfigService.loadCountries());
                    break;
                case NBlogCategoryPageType.typeId:
                    // for blog category load all posts
                    params.loader.add(this.loadBlogCategoryPosts(safeParams));
                    // for blog category load all blog categories categories
                    params.loader.add(pageResuorce.loadBlogCategories(2, safeParams));
                    params.loader.add(this.loadBlogCategoryPosts(safeParams));
                    break;
                case NBlogRootPageType.typeId:
                    // for blog category load all posts
                    params.loader.add(pageResuorce.loadBlogPosts(2, safeParams));
                    // for blog category load all blog categories categories
                    params.loader.add(pageResuorce.loadBlogCategories(2, safeParams));
                    break;
                case NMaterialCalculatorPageType.typeId:
                    // for material calculator load all categories
                    params.loader.add(
                        this.iCmsBlockResource.retrieve({ bareUrl: NMaterialsCalculatorCms.BLOCK_ID, createOnError: true }, safeParams)
                    );
                    params.loader.add(pageResuorce.loadCalculators(2, safeParams));
                    break;
            }
        }
    }

    /**
     * Loads categories (tags) of a blog post
     * blog-post type only
     */
    private async loadBlogPostCategories(params: TLoadDependeciesParams): Promise<void> {
        const categoryIds = this.getBlogPostCategoryIds();
        if (categoryIds?.length) {
            await this.resources.page.loadAllIds(categoryIds, 2, params);
        }
    }
    /**
     * Loads posts linked to a blog category (including linked by tags entry)
     * blog-category type only
     */
    private async loadBlogCategoryPosts(params: TLoadDependeciesParams): Promise<void> {
        if (this.rawData?.pageType === NBlogCategoryPageType.typeId) {
            this.tagLinkedPosts = (await this.resources.page.find(
                { filter: { 'typeData.tags': this.id }, verbosity: 2 },
                params
            )) as LocalPage<IPageDataV2<any>>[];
            await this.loadChildren(2, params);
        }
    }
}
